import { useEffect, useMemo, useState } from 'react';
import {
  INTERRUPT_OPTIONS,
  TAG_LABELS,
  getStepsForMode,
} from '../content';
import {
  isCommsStep,
  isFocusStep,
  isHighlightStep,
  isRankStep,
  isResearchStep,
  isRoleScanStep,
  isTagStep,
  isTimeboxStep,
} from '../steps';
import { itemsWithTag, sortByRankWithinTag } from '../ranking';
import {
  buildNorthStarTimeboxEvent,
  defaultTimeboxStart,
  downloadICS,
  generateGoogleCalendarLink,
} from '../timebox';
import type { UseMorningBootReturn } from '../useMorningBoot';
import type { BootItem, BootTag } from '../types';
import { THEME_CLASSES, type MorningBootTheme } from './theme';

interface MorningBootWizardProps {
  boot: UseMorningBootReturn;
  theme: MorningBootTheme;
  title?: string;
  subtitle?: string;
  dashboardPath?: string;
  onCompleteNavigate?: () => void;
}

function TagButton({
  tag,
  active,
  onClick,
  theme,
}: {
  tag: BootTag;
  active: boolean;
  onClick: () => void;
  theme: MorningBootTheme;
}) {
  const t = THEME_CLASSES[theme];
  const tagClass =
    tag === 'A' ? t.tagA : tag === 'T' ? t.tagT : t.tagG;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-0.5 rounded border text-xs font-medium ${tagClass} ${active ? t.tagActive : ''}`}
    >
      {TAG_LABELS[tag].short}
    </button>
  );
}

function ItemListEditor({
  items,
  theme,
  onAdd,
  onRemove,
  onUpdate,
  showTags,
  onToggleTag,
  showHighlight,
  highlightedIds,
  onToggleHighlight,
}: {
  items: BootItem[];
  theme: MorningBootTheme;
  onAdd: (text: string) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, text: string) => void;
  showTags?: boolean;
  onToggleTag?: (id: string, tag: BootTag) => void;
  showHighlight?: boolean;
  highlightedIds?: string[];
  onToggleHighlight?: (id: string) => void;
}) {
  const t = THEME_CLASSES[theme];
  const [draft, setDraft] = useState('');

  const handleAdd = () => {
    if (!draft.trim()) return;
    onAdd(draft);
    setDraft('');
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          className={t.input}
          placeholder="Add an item..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <button type="button" className={t.buttonPrimary} onClick={handleAdd}>
          Add
        </button>
      </div>
      {items.length === 0 ? (
        <p className={t.textMuted}>No items yet. Start purging everything on your plate.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id} className={`${t.cardMuted} flex flex-col gap-2`}>
              <div className="flex gap-2 items-start">
                <input
                  className={`${t.input} flex-1`}
                  value={item.text}
                  onChange={(e) => onUpdate(item.id, e.target.value)}
                />
                <button type="button" className={t.buttonGhost} onClick={() => onRemove(item.id)}>
                  Remove
                </button>
              </div>
              {(showTags || showHighlight) && (
                <div className="flex flex-wrap gap-2 items-center">
                  {showTags && onToggleTag && (
                    <>
                      <TagButton tag="A" active={item.tags.includes('A')} onClick={() => onToggleTag(item.id, 'A')} theme={theme} />
                      <TagButton tag="T" active={item.tags.includes('T')} onClick={() => onToggleTag(item.id, 'T')} theme={theme} />
                      <TagButton tag="G" active={item.tags.includes('G')} onClick={() => onToggleTag(item.id, 'G')} theme={theme} />
                    </>
                  )}
                  {showHighlight && onToggleHighlight && (
                    <button
                      type="button"
                      className={`${t.buttonGhost} ${highlightedIds?.includes(item.id) ? t.accent : ''}`}
                      onClick={() => onToggleHighlight(item.id)}
                    >
                      {highlightedIds?.includes(item.id) ? '★ Highlighted' : 'Highlight'}
                    </button>
                  )}
                  {item.source === 'imported' || item.source === 'suggested' ? (
                    <span className={`${t.badge} ${t.textMuted}`}>imported</span>
                  ) : null}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RankList({
  tag,
  items,
  theme,
  boot,
}: {
  tag: BootTag;
  items: BootItem[];
  theme: MorningBootTheme;
  boot: UseMorningBootReturn;
}) {
  const t = THEME_CLASSES[theme];
  const tagged = itemsWithTag(items, tag);
  const [order, setOrder] = useState<string[]>(() =>
    sortByRankWithinTag(items, tag).map((i) => i.id)
  );

  useEffect(() => {
    setOrder(sortByRankWithinTag(items, tag).map((i) => i.id));
  }, [items, tag]);

  const move = (index: number, direction: -1 | 1) => {
    const next = [...order];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    const [removed] = next.splice(index, 1);
    next.splice(target, 0, removed);
    setOrder(next);
    boot.rankItemsForTag(tag, next);
  };

  if (tagged.length === 0) {
    return (
      <div className={t.cardMuted}>
        <div className={t.label}>{TAG_LABELS[tag].label}</div>
        <p className={t.textMuted}>No items tagged {TAG_LABELS[tag].short}.</p>
      </div>
    );
  }

  return (
    <div className={t.cardMuted}>
      <div className={t.label}>{TAG_LABELS[tag].label}</div>
      <p className={`${t.textMuted} mb-3`}>{TAG_LABELS[tag].description}</p>
      <ol className="space-y-2">
        {order.map((id, index) => {
          const item = items.find((i) => i.id === id);
          if (!item) return null;
          return (
            <li key={id} className="flex items-center gap-2">
              <span className={`${t.badge} ${t.textMuted}`}>#{index + 1}</span>
              <span className={`${t.text} flex-1`}>{item.text}</span>
              <button type="button" className={t.buttonGhost} onClick={() => move(index, -1)} disabled={index === 0}>
                ↑
              </button>
              <button
                type="button"
                className={t.buttonGhost}
                onClick={() => move(index, 1)}
                disabled={index === order.length - 1}
              >
                ↓
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function FocusTimer({ itemText, theme }: { itemText: string; theme: MorningBootTheme }) {
  const t = THEME_CLASSES[theme];
  const [secondsLeft, setSecondsLeft] = useState(30 * 60);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running) return;
    if (secondsLeft <= 0) return;
    const id = window.setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [running, secondsLeft]);

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <div className={`${t.cardMuted} text-center space-y-4`}>
      <p className={t.text}>{itemText}</p>
      <div className={`text-4xl font-mono ${t.accent}`}>
        {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
      </div>
      <div className="flex justify-center gap-2">
        <button type="button" className={t.buttonPrimary} onClick={() => setRunning(true)}>
          Start focus
        </button>
        <button
          type="button"
          className={t.buttonSecondary}
          onClick={() => {
            setRunning(false);
            setSecondsLeft(30 * 60);
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
}

export function MorningBootWizard({
  boot,
  theme,
  title = 'Morning Boot Routine',
  subtitle = 'DEGAP Lite — decide what matters before the day decides for you.',
  dashboardPath,
  onCompleteNavigate,
}: MorningBootWizardProps) {
  const t = THEME_CLASSES[theme];
  const { session } = boot;
  const [interruptText, setInterruptText] = useState('');
  const [timeboxStart, setTimeboxStart] = useState(() => defaultTimeboxStart().toISOString());

  useEffect(() => {
    if (session && session.step === 1) {
      boot.refreshSuggestions();
    }
  }, [session?.id, session?.step]);

  const progressPct = session ? Math.round((session.step / boot.maxStep) * 100) : 0;

  const topItems = useMemo(() => {
    if (!session) return { a: null, t: null, g: null };
    const find = (id?: string) => session.items.find((i) => i.id === id);
    return {
      a: find(session.topThree.a1),
      t: find(session.topThree.t2),
      g: find(session.topThree.g3),
    };
  }, [session]);

  if (!session) {
    return (
      <div className={t.card}>
        <p className={t.text}>No active session. Refresh to start today&apos;s Morning Boot.</p>
      </div>
    );
  }

  if (session.completedAt) {
    return (
      <div className={t.card}>
        <h2 className={t.heading}>Morning Boot complete</h2>
        <p className={t.subheading}>Your three priorities for today:</p>
        <ul className="mt-4 space-y-3">
          {topItems.a && (
            <li className={t.cardMuted}>
              <span className={t.label}>1 · Avoidance</span>
              <p className={t.text}>{topItems.a.text}</p>
            </li>
          )}
          {topItems.t && (
            <li className={t.cardMuted}>
              <span className={t.label}>2 · Time-sensitive</span>
              <p className={t.text}>{topItems.t.text}</p>
            </li>
          )}
          {topItems.g && (
            <li className={t.cardMuted}>
              <span className={t.label}>3 · North Star</span>
              <p className={t.text}>{topItems.g.text}</p>
            </li>
          )}
        </ul>
        {dashboardPath ? (
          <a href={dashboardPath} className={`${t.buttonPrimary} inline-block mt-6`}>
            Back to dashboard
          </a>
        ) : onCompleteNavigate ? (
          <button type="button" className={`${t.buttonPrimary} mt-6`} onClick={onCompleteNavigate}>
            Done
          </button>
        ) : null}
      </div>
    );
  }

  const step = session.step;
  const mode = session.mode;

  return (
    <div className={t.page}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className={t.heading}>{title}</h1>
          <p className={t.subheading}>{subtitle}</p>
          {boot.northStarText && (
            <p className={`${t.textMuted} mt-2`}>
              <span className={t.label}>{boot.northStarLabel}: </span>
              {boot.northStarText}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className={t.label}>Mode</label>
          <select
            className={t.input}
            value={mode}
            onChange={(e) => boot.setBootMode(e.target.value as 'basic' | 'advanced')}
          >
            <option value="basic">Basic (5 steps)</option>
            <option value="advanced">Advanced (9 steps)</option>
          </select>
        </div>
      </div>

      <div>
        <div className="flex justify-between mb-1">
          <span className={t.label}>
            Step {step} of {boot.maxStep}
          </span>
          <span className={t.textMuted}>{progressPct}%</span>
        </div>
        <div className={t.progressBar}>
          <div className={t.progressFill} style={{ width: `${progressPct}%` }} />
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {getStepsForMode(mode).map((s) => (
            <button
              key={s.step}
              type="button"
              className={`${t.buttonGhost} ${s.step === step ? t.accent : ''}`}
              onClick={() => boot.goToStep(s.step)}
            >
              {s.step}
            </button>
          ))}
        </div>
      </div>

      {boot.stepContent && (
        <div className={t.card}>
          <h2 className={`text-lg font-semibold ${t.text}`}>{boot.stepContent.title}</h2>
          <p className={`${t.textMuted} mt-2`}>{boot.stepContent.instruction}</p>
          {boot.stepContent.coachRail && (
            <p className={`${t.cardMuted} mt-3 text-sm`}>{boot.stepContent.coachRail}</p>
          )}
        </div>
      )}

      {(step === 1 && mode === 'basic') || (step === 1 && mode === 'advanced') ? (
        <div className={t.card}>
          {mode === 'advanced' && boot.state.backlog.length > 0 && (
            <div className="mb-4">
              <div className={t.label}>From backlog ({boot.state.backlog.length})</div>
              <p className={t.textMuted}>Items from your backlog are already in today&apos;s list.</p>
            </div>
          )}
          <ItemListEditor
            items={session.items}
            theme={theme}
            onAdd={(text) => boot.addSessionItem(text)}
            onRemove={boot.removeSessionItem}
            onUpdate={(id, text) => boot.updateSessionItem(id, { text, clarified: text.trim().length > 0 })}
          />
          <button type="button" className={`${t.buttonSecondary} mt-4`} onClick={() => boot.refreshSuggestions()}>
            Import suggestions
          </button>
        </div>
      ) : null}

      {isRoleScanStep(mode, step) && (
        <div className={t.card}>
          <div className="flex flex-wrap gap-2">
            {boot.suggestedRoles.map((role) => (
              <button
                key={role}
                type="button"
                className={`${t.buttonSecondary} ${session.advanced?.rolesScanned.includes(role) ? t.accent : ''}`}
                onClick={() => boot.toggleRoleScanned(role)}
              >
                {role}
              </button>
            ))}
          </div>
          <ItemListEditor
            items={session.items}
            theme={theme}
            onAdd={(text) => boot.addSessionItem(text, { metadata: { role: 'scan' } })}
            onRemove={boot.removeSessionItem}
            onUpdate={(id, text) => boot.updateSessionItem(id, { text })}
          />
        </div>
      )}

      {isCommsStep(mode, step) && (
        <div className={t.card}>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={session.advanced?.commsReviewed ?? false}
              onChange={(e) => boot.setCommsReviewed(e.target.checked)}
            />
            <span className={t.text}>I reviewed calendar and recent communications</span>
          </label>
          <div className="mt-4">
            <ItemListEditor
              items={session.items}
              theme={theme}
              onAdd={(text) => boot.addSessionItem(text, { metadata: { commitment: true } })}
              onRemove={boot.removeSessionItem}
              onUpdate={(id, text) => boot.updateSessionItem(id, { text })}
            />
          </div>
        </div>
      )}

      {isResearchStep(mode, step) && (
        <div className={t.card}>
          <ItemListEditor
            items={session.items.filter((i) => i.metadata?.research)}
            theme={theme}
            onAdd={(text) => boot.addResearchItem(text)}
            onRemove={boot.removeSessionItem}
            onUpdate={(id, text) => boot.updateSessionItem(id, { text })}
          />
        </div>
      )}

      {isHighlightStep(mode, step) && (
        <div className={t.card}>
          <ItemListEditor
            items={session.items}
            theme={theme}
            showHighlight
            highlightedIds={session.advanced?.highlightedItemIds}
            onToggleHighlight={boot.toggleHighlight}
            onAdd={(text) => boot.addSessionItem(text, { metadata: { lever: true } })}
            onRemove={boot.removeSessionItem}
            onUpdate={(id, text) => boot.updateSessionItem(id, { text })}
          />
        </div>
      )}

      {isTagStep(mode, step) && (
        <div className={t.card}>
          <ItemListEditor
            items={session.items}
            theme={theme}
            showTags
            onToggleTag={boot.toggleItemTag}
            onAdd={(text) => boot.addSessionItem(text)}
            onRemove={boot.removeSessionItem}
            onUpdate={(id, text) => boot.updateSessionItem(id, { text, clarified: true })}
          />
        </div>
      )}

      {isRankStep(mode, step) && (
        <div className="space-y-4">
          <RankList tag="A" items={session.items} theme={theme} boot={boot} />
          <RankList tag="T" items={session.items} theme={theme} boot={boot} />
          <RankList tag="G" items={session.items} theme={theme} boot={boot} />
          <div className={t.cardMuted}>
            <p className={t.text}>{boot.stepContent?.coachRail}</p>
          </div>
          <button type="button" className={t.buttonPrimary} onClick={() => boot.confirmTopThree()}>
            Confirm top three
          </button>
        </div>
      )}

      {isTimeboxStep(mode, step) && topItems.g ? (
        <div className={t.card}>
          <p className={t.text}>North Star item: {topItems.g.text}</p>
          <label className={`${t.label} block mt-4`}>Timebox start</label>
          <input
            type="datetime-local"
            className={`${t.input} mt-1`}
            value={timeboxStart.slice(0, 16)}
            onChange={(e) => setTimeboxStart(new Date(e.target.value).toISOString())}
          />
          <div className="flex flex-wrap gap-2 mt-4">
            <a
              href={generateGoogleCalendarLink(
                buildNorthStarTimeboxEvent(topItems.g.text, new Date(timeboxStart))
              )}
              target="_blank"
              rel="noreferrer"
              className={t.buttonPrimary}
              onClick={() => boot.scheduleTimebox(timeboxStart, 'google')}
            >
              Add to Google Calendar
            </a>
            <button
              type="button"
              className={t.buttonSecondary}
              onClick={() => {
                downloadICS(buildNorthStarTimeboxEvent(topItems.g!.text, new Date(timeboxStart)));
                boot.scheduleTimebox(timeboxStart, 'ics');
              }}
            >
              Download ICS
            </button>
          </div>
        </div>
      ) : null}

      {isFocusStep(mode, step) && topItems.a && (
        <div className={t.card}>
          <FocusTimer itemText={topItems.a.text} theme={theme} />
        </div>
      )}

      <div className={t.card}>
        <div className={t.label}>New item appeared?</div>
        <div className="flex gap-2 mt-2">
          <input
            className={t.input}
            placeholder="Capture the interrupt..."
            value={interruptText}
            onChange={(e) => setInterruptText(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {INTERRUPT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={t.buttonSecondary}
              title={opt.description}
              onClick={() => {
                boot.handleInterrupt(opt.id as 'slot' | 'guesstimate' | 'backlog', interruptText);
                setInterruptText('');
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          className={t.buttonSecondary}
          disabled={step <= 1}
          onClick={() => boot.goBackStep()}
        >
          Back
        </button>
        {step < boot.maxStep ? (
          <button type="button" className={t.buttonPrimary} onClick={() => boot.advanceStep()}>
            Next step
          </button>
        ) : (
          <button
            type="button"
            className={t.buttonPrimary}
            onClick={() => {
              boot.complete();
              onCompleteNavigate?.();
            }}
          >
            Complete Morning Boot
          </button>
        )}
      </div>
    </div>
  );
}
